import { DeviceContainerStateControllerService } from "../../../services/device-container-state-controller.service";
import { IncidentHistoricalControllerService } from "../../../services/incident-historical-controller.service";
import { DataDecompressorService } from "../../../services/data-decompressor.service";
import { SiteControllerService } from "../../../services/site-controller.service";
import { UserRightsService } from "../../../services/user-rights.service";
import { GeoUtils } from "../../../services/geo-utils";

import { CurrentStateFilterDTO } from "../../../models/current-state-filter";
import { ContainerTypeDTO } from "../../../models/container-type";
import { CurrentStateDTO } from "../../../models/current-state";
import { SiteDTO } from "../../../models/site";

import {
  LIST,
  ExportExcelComponent
} from "../../utils/export-excel/export-excel.component";

import { animate, style, transition, trigger } from "@angular/animations";
import { MatDialog } from "@angular/material/dialog";
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import { InfiniteScrollDirective } from "../../../../app/directives/infinite-scroll.directive";
import { SidenavFiltersComponent } from "../../sidenav/sidenav-filters/sidenav-filters.component";
import { NgClass } from "@angular/common";
import { DetailComponent } from "./detail/detail.component";
import { HeaderListComponent } from "./header-list/header-list.component";
import { StateDisplayerColumnNamesComponent } from "../../utils/state-displayer-column-names/state-displayer-column-names.component";
import { InfiniteScrollDirective as InfiniteScrollDirective_1 } from "../../../directives/infinite-scroll.directive";
import { StateDisplayerSkeletonLineComponent } from "../../utils/state-displayer-skeleton-line/state-displayer-skeleton-line.component";
import { StateDisplayerComponent } from "../../utils/state-displayer/state-displayer.component";
import { IncidentSidenavComponent } from "../../sidenav/incident-sidenav/incident-sidenav.component";
import { TranslateModule } from "@ngx-translate/core";
import { MAP_LIST_FILTER_CONFIG } from "../../../services/filter-sync-configs";
import { RecordRoute } from "../../../services/record-route";
import { FleetFilter, TrackerFleetFilters } from "../tracker-fleet-filters";
import { NavigationNoticeService } from "../../../services/navigation-notice.service";
@Component({
  selector: "app-list",
  animations: [
    trigger("enterAnimation", [
      transition(":enter", [
        style({ opacity: 0 }),
        animate("100ms", style({ opacity: 1 }))
      ]),
      transition(":leave", [
        style({ opacity: 1 }),
        animate("600ms", style({ opacity: 0 }))
      ])
    ])
  ],
  templateUrl: "./list.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./list.component.scss"],
  imports: [
    SidenavFiltersComponent,
    NgClass,
    DetailComponent,
    HeaderListComponent,
    StateDisplayerColumnNamesComponent,
    InfiniteScrollDirective_1,
    StateDisplayerSkeletonLineComponent,
    StateDisplayerComponent,
    ExportExcelComponent,
    IncidentSidenavComponent,
    TranslateModule
  ]
})
export class ListComponent
  extends TrackerFleetFilters
  implements OnInit, OnDestroy
{
  // Internal state for the in-page detail panel (no longer parent-bound inputs
  // after the router migration; the list detail child two-way-binds to these).
  infoCurrentState: CurrentStateDTO;
  showInfo: boolean;

  @ViewChild(InfiniteScrollDirective) infiniteScroll: InfiniteScrollDirective;

  public sortedCurrentStateList: CurrentStateDTO[] | undefined;
  public totalFiteredCurrentStates: number;
  public fetchingContainer: boolean;
  public exportType: string = LIST;
  public listSites: SiteDTO[] = [];
  public pageSize: number = 30;
  public showMap: boolean;

  public loading: boolean = false;
  public loadingProgress: number = 0;

  public exportData: CurrentStateFilterDTO = {
    requestId: 0,
    containerTypes: undefined
  };

  private containerTypes: Array<ContainerTypeDTO>;
  private mustRefreshList: boolean = true;
  private timeoutId: NodeJS.Timeout;
  private pageIndex: number = 0;
  private requestId: number = 0;
  private allChecked: boolean = false;

  private progressTimeout: NodeJS.Timeout;
  private progressInterval: NodeJS.Timeout;

  private index: number = 30;
  private readonly STATE_BATCH: number = 60;
  /** The `/list/tracker/:tracker_id` segment: what it means to be showing one. */
  private trackerRoute: RecordRoute;
  /** The `/list/site/:site_code` segment, the tracker segment's sibling. */
  private siteRoute: RecordRoute;
  /**
   * Deep-link target (`/list/site/:site_code`) that cannot be judged yet: only
   * `listSites` can say whether a code names a site, and on a cold load that fetch
   * has not landed. `initDatas` is where it is applied.
   */
  private pendingSiteCode: string | undefined;

  constructor(
    public dialog: MatDialog,
    private siteServices: SiteControllerService,
    private dataDecompressorService: DataDecompressorService,
    public _geoUtils: GeoUtils,
    public _deviceContainerStateService: DeviceContainerStateControllerService,
    public _incidentHistoricalControllerService: IncidentHistoricalControllerService,
    public _userRightsService: UserRightsService,
    private navigationNotice: NavigationNoticeService
  ) {
    super();
    this.showMap = false;
    // Hydrate from here rather than from the base constructor: a hydrated
    // incident selection resolves its devices straight away, which reaches
    // refreshAfterFilterChange — and that needs exportData to already exist.
    this.hydrateFilters();
    this.trackerRoute = new RecordRoute(
      this.route,
      this.router,
      this.navigationNotice,
      {
        paramKey: "tracker_id",
        basePath: ["/list"],
        // The record is one segment deeper than the list: /list/tracker/:tracker_id.
        recordPath: ["/list", "tracker"],
        open: (deviceId: string) => this.openDetailFromRoute(deviceId),
        // The URL no longer names a tracker, so hide the detail - but do NOT
        // navigate. The route has already moved, and /list is not necessarily where
        // it moved to: Back from /list/tracker/:id lands on /list/site/:code just as
        // often, and a navigate here would eject the user from the site view the
        // router had just restored.
        close: () => {
          if (this.showInfo) {
            this.teardownDetail();
          }
        },
        // The list has no detail-only params, so carrying the filters over is
        // all that is needed.
        queryParamsHandling: "merge"
      }
    );
    this.siteRoute = new RecordRoute(
      this.route,
      this.router,
      this.navigationNotice,
      {
        paramKey: "site_code",
        basePath: ["/list"],
        // Sibling of the tracker segment, not a parent of it: /list/site/:site_code.
        recordPath: ["/list", "site"],
        open: (code: string) => this.openSiteFromRoute(code),
        close: () => this.closeSiteFromRoute(),
        queryParamsHandling: "merge"
      }
    );
  }

  /**
   * A filter changed: tell the export about it, then re-fetch the page.
   */
  protected refreshAfterFilterChange(filter: FleetFilter): void {
    this.syncExportData(filter);
    this.mustRefreshList = true;
    this.updateList();
  }

  /**
   * Any new selection means the rows below are about to be replaced, so the
   * scrollbar goes back to the top and the paging window resets — whether or not
   * the change turns out to need a fetch.
   */
  protected override onFilterSet(_filter: FleetFilter): void {
    this.resetInfiniteScrollData();
  }

  /**
   * The export takes the same filters the list does, under its own names. This
   * table is the whole translation: it used to be eight inline spreads, one per
   * change handler, so a new filter had to be remembered in two places and an
   * export key silently went stale when it was not.
   */
  private syncExportData(filter: FleetFilter): void {
    switch (filter) {
      case "containers":
        this.exportData = {
          ...this.exportData,
          filterContainers: this.filterContainers,
          containerTypes: this.containerTypes
        };
        break;
      // Both device filters land on the one export key: whichever of them is
      // driving the list is the device list the export has to ship.
      case "devices":
        this.exportData = {
          ...this.exportData,
          filterDevicesId: this.filterDevices
        };
        break;
      case "incidentDevices":
        this.exportData = {
          ...this.exportData,
          filterDevicesId: this.filterIncidentDevices
        };
        break;
      case "lastSites":
        this.exportData = {
          ...this.exportData,
          filterLastSites: this.filterLastSites
        };
        break;
      case "statusDays":
        this.exportData = {
          ...this.exportData,
          filterStatusDays: this.filterStatusDays
        };
        break;
      case "confidenceRadius":
        this.exportData = {
          ...this.exportData,
          filterConfidenceRadius: this.filterConfidenceRadius
        };
        break;
      case "devicesTypes":
        this.exportData = {
          ...this.exportData,
          filterDevicesTypes: this.filterDevicesTypes
        };
        break;
      case "containerCategory":
        this.exportData = {
          ...this.exportData,
          filterContainerCategory: this.filterContainerCategory
        };
        break;
      case "torn":
        this.exportData = { ...this.exportData, filterTorn: this.filterTorn };
        break;
      case "unknown":
        this.exportData = {
          ...this.exportData,
          filterUnknown: this.filterUnknown
        };
        break;
      case "colorStatus":
        this.exportData = { ...this.exportData, filtersColor: this.statusList };
        break;
    }
  }

  ngOnInit() {
    this.initDatas();
    // Seed the colour filter before subscribing, so the mirror below publishes a
    // resolved `status`.
    this.seedColorStatus();
    // Publish the whole filter set now that `status` is resolved.
    this.mirrorFilters();
    this.initStore();
    this.initExport();

    this.mustRefreshList = true;

    // Route-driven device detail: /list/tracker/:tracker_id (= deviceId) opens
    // that device's inline history detail, and /list/site/:site_code shows one
    // site's devices. Both react to deep-links, page refresh and back/forward.
    // On-page row clicks funnel through the same URL (see showInfoContainer), so
    // the guard in openDetailFromRoute short-circuits the re-emit and avoids a
    // second fetch.
    //
    // The tracker goes FIRST, and the order is load-bearing: the two segments hang
    // off one paramMap, so a hop between them emits once and both handlers run. On
    // site -> tracker the tracker's open has then already moved the snapshot, which
    // is what lets the site's close skip a reload nobody would look at.
    this.subscription.add(this.trackerRoute.follow());
    this.subscription.add(this.siteRoute.follow());
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }

    if (this.progressTimeout) {
      clearTimeout(this.progressTimeout);
    }

    this.subscription.unsubscribe();
  }

  private initDatas(): void {
    this.subscription.add(
      this.siteServices.getSites().subscribe((sites) => {
        this.listSites = sites;
        // The site the URL names can only be resolved now: until this landed every
        // code looked unknown.
        this.applyPendingSite();
      })
    );
  }

  private initStore(): void {
    this.subscription.add(
      this.store.ctTypes$.subscribe((ctTypesData: ContainerTypeDTO[]) => {
        this.containerTypes = ctTypesData;
        this.exportData = {
          ...this.exportData,
          containerTypes: ctTypesData
        };
        this.mustRefreshList = true;
        this.updateList();
      })
    );

    this.followColorStatusStore();
  }

  private initExport(): void {
    this.exportData = {
      ...this.exportData,
      filterContainers: this.filterContainers,
      containerTypes: this.containerTypes,
      siteId: this.filterSiteCode,
      filtersColor: this.statusList,
      filterDevicesId: !this.filterIncidentDevices?.length
        ? this.filterDevices
        : this.filterIncidentDevices,
      filterLastSites: this.filterLastSites,
      filterTorn: this.filterTorn,
      filterUnknown: this.filterUnknown,
      filterDevicesTypes: this.filterDevicesTypes,
      filterContainerCategory: this.filterContainerCategory,
      filterConfidenceRadius: this.filterConfidenceRadius,
      filterStatusDays: this.filterStatusDays
    };
  }

  /** The detail's own close affordance: hide it, and drop its URL segment. */
  public reloadList(event: any) {
    if (!event) {
      this.teardownDetail();
      this.trackerRoute.toList();
    }
  }

  /** Hide the detail, whatever asked for it. Navigating is the caller's business. */
  private teardownDetail(): void {
    this.infoCurrentState = null;
    this.showInfo = false;
  }

  /**
   * Enter or leave the site view. Every caller is a user action - the filter
   * sidenav or the header - and all any of them does is navigate: the site is
   * `/list/site/:site_code`, and applying it is the route subscription's job, so a
   * click and a deep link take exactly the same path.
   * @param event {string} Site code to open, or undefined to go back to the fleet.
   */
  public siteRequest(event?: string) {
    if (event) {
      this.siteRoute.goTo(event);
    } else {
      this.siteRoute.toList();
    }
  }

  /**
   * The URL names a site: show its devices. Judging the code needs `listSites`,
   * which a cold deep-link has not fetched yet - so it is parked in
   * `pendingSiteCode` and `initDatas` finishes the job.
   * @param code {string} The `:site_code` param.
   */
  private openSiteFromRoute(code: string): void {
    this.pendingSiteCode = code;
    this.resetInfiniteScrollData();
    if (this.listSites.length) {
      this.applyPendingSite();
    }
  }

  /**
   * Fetch the parked site's devices now that `listSites` can vouch for the code. A
   * code that names no site explains itself and drops back to the fleet.
   */
  private applyPendingSite(): void {
    const code: string = this.pendingSiteCode;
    if (!code) return;
    this.pendingSiteCode = undefined;

    if (!this.listSites.some((site) => site.code === code)) {
      this.siteRoute.notFound();
      return;
    }
    if (this.filterSiteCode === code) return;

    this.filterSiteCode = code;
    this.exportData = { ...this.exportData, siteId: code };
    this.mustRefreshList = true;
    this.updateList();
  }

  /**
   * The URL names no site: the whole fleet is what should be listed. Never
   * navigates - the router has already moved, and on the site -> tracker hop it
   * moved to /list/tracker/:id, not to /list. That hop skips the re-fetch too: the
   * detail replaces the rows entirely, so nobody would see the ones it would load.
   */
  private closeSiteFromRoute(): void {
    this.resetInfiniteScrollData();
    this.filterSiteCode = undefined;
    this.pendingSiteCode = undefined;
    this.exportData = { ...this.exportData, siteId: undefined };
    this.mustRefreshList = true;
    if (!this.trackerRoute.currentId()) {
      this.updateList();
    }
  }

  private updateList(isPageChanged?: boolean) {
    this.requestId++;
    const id: number = this.requestId;
    if (this.timeoutId) clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      if (this.requestId === id && this.mustRefreshList) {
        if (!isPageChanged) {
          this.pageIndex = 0;
        }
        if (this.showCreateIncident) this.showCreateIncident = false;
        this.listIncidentDevices = [];
        this.mustRefreshList = false;
        this.filterSiteCode
          ? this.sendSiteRequest(isPageChanged)
          : this.sendListRequest(isPageChanged);
      }
    }, 1000);
  }

  /**
   * Simaulates loading progress for site data.
   */
  private simulateProgress(): void {
    this.loadingProgress = 0;

    this.progressInterval = setInterval(() => {
      this.loadingProgress += 10;
      if (this.loadingProgress >= 90) {
        clearInterval(this.progressInterval);
      }
    }, 150);
  }

  /**
   * Stop the loading progress.
   */
  private endingProgress(): void {
    this.progressTimeout = setTimeout(() => {
      this.loadingProgress = 100;
      setTimeout(() => {
        this.loadingProgress = 0;
      }, 500);
    }, 200);
  }

  public loadMore = () => {
    this.filterSiteCode ? this.loadMoreSite() : this.loadMoreList();
  };

  public loadMoreSite() {
    if (!this.loading && this.index < this.totalFiteredCurrentStates) {
      this.loading = true;

      this.subscription.add(
        this._deviceContainerStateService
          .getCurrentStatesBySite({
            requestId: this.requestId,
            filterContainers: this.filterContainers,
            containerTypes: this.containerTypes,
            siteId: this.filterSiteCode,
            limit: this.STATE_BATCH,
            skip: this.index,
            filtersColor: this.statusList,
            filterDevicesId: !this.filterIncidentDevices?.length
              ? this.filterDevices
              : this.filterIncidentDevices,
            filterLastSites: this.filterLastSites,
            filterConfidenceRadius: this.filterConfidenceRadius,
            filterDevicesTypes: this.filterDevicesTypes,
            filterContainerCategory: this.filterContainerCategory,
            filterStatusDays: this.filterStatusDays,
            filterTorn: this.filterTorn,
            filterUnknown: this.filterUnknown
          })
          .subscribe((result) => {
            this.dataDecompressorService
              .unpack(result.partialCurrentStates)
              .then((partialCurrentStates) => {
                if (result.requestId === this.requestId) {
                  this.sortedCurrentStateList.push(...partialCurrentStates);
                  this.index += this.STATE_BATCH;
                  if (
                    this.filterIncidentDevices?.length ||
                    this.filterDevices?.length
                  ) {
                    this.totalFiteredCurrentStates =
                      this.sortedCurrentStateList.length;
                  }
                }
              });

            this.loading = false;
          })
      );
    }
  }

  public loadMoreList() {
    if (!this.loading && this.index < this.totalFiteredCurrentStates) {
      this.loading = true;

      this.subscription.add(
        this._deviceContainerStateService
          .getAllCurrentStatesByContainerType({
            requestId: this.requestId,
            filterContainers: this.filterContainers,
            containerTypes: this.containerTypes,
            limit: this.STATE_BATCH,
            skip: this.index,
            filtersColor: this.statusList,
            filterDevicesId: !this.filterIncidentDevices?.length
              ? this.filterDevices
              : this.filterIncidentDevices,
            filterLastSites: this.filterLastSites,
            filterStatusDays: this.filterStatusDays,
            filterConfidenceRadius: this.filterConfidenceRadius,
            filterDevicesTypes: this.filterDevicesTypes,
            filterContainerCategory: this.filterContainerCategory,
            filterTorn: this.filterTorn,
            filterUnknown: this.filterUnknown
          })
          .subscribe((result) => {
            this.dataDecompressorService
              .unpack(result.partialCurrentStates)
              .then((partialCurrentStates) => {
                if (result.requestId === this.requestId) {
                  this.sortedCurrentStateList.push(...partialCurrentStates);
                  this.index += this.STATE_BATCH;
                  if (
                    this.filterIncidentDevices?.length ||
                    this.filterDevices?.length
                  ) {
                    this.totalFiteredCurrentStates =
                      this.sortedCurrentStateList.length;
                  }
                }
              });

            this.loading = false;
          })
      );
    }
  }

  public sendSiteRequest(_isPageChanged?: boolean) {
    this.simulateProgress();

    this.subscription.add(
      this._deviceContainerStateService
        .getCurrentStatesBySite({
          requestId: this.requestId,
          filterContainers: this.filterContainers,
          containerTypes: this.containerTypes,
          siteId: this.filterSiteCode,
          limit: this.pageSize,
          skip: this.pageSize * this.pageIndex,
          filtersColor: this.statusList,
          filterDevicesId: !this.filterIncidentDevices?.length
            ? this.filterDevices
            : this.filterIncidentDevices,
          filterLastSites: this.filterLastSites,
          filterStatusDays: this.filterStatusDays,
          filterConfidenceRadius: this.filterConfidenceRadius,
          filterDevicesTypes: this.filterDevicesTypes,
          filterContainerCategory: this.filterContainerCategory,
          filterTorn: this.filterTorn,
          filterUnknown: this.filterUnknown
        })
        .subscribe((result) => {
          this.dataDecompressorService
            .unpack(result.partialCurrentStates)
            .then((partialCurrentStates) => {
              if (result.requestId === this.requestId) {
                this.sortedCurrentStateList = partialCurrentStates;
              }

              this.endingProgress();
              this.totalFiteredCurrentStates = result["currentStateLength"];
            });
        })
    );
  }

  public sendListRequest(isPageChanged?: boolean) {
    this.simulateProgress();

    this.subscription.add(
      this._deviceContainerStateService
        .getAllCurrentStatesByContainerType({
          requestId: this.requestId,
          filterContainers: this.filterContainers,
          containerTypes: this.containerTypes,
          limit: this.pageSize,
          skip: this.pageSize * this.pageIndex,
          filtersColor: this.statusList,
          filterDevicesId: !this.filterIncidentDevices?.length
            ? this.filterDevices
            : this.filterIncidentDevices,
          filterLastSites: this.filterLastSites,
          filterStatusDays: this.filterStatusDays,
          filterConfidenceRadius: this.filterConfidenceRadius,
          filterDevicesTypes: this.filterDevicesTypes,
          filterContainerCategory: this.filterContainerCategory,
          filterTorn: this.filterTorn,
          filterUnknown: this.filterUnknown
        })
        .subscribe((result) => {
          this.dataDecompressorService
            .unpack(result.partialCurrentStates)
            .then((partialCurrentStates) => {
              if (result.requestId === this.requestId) {
                this.sortedCurrentStateList = partialCurrentStates;
                if (
                  this.filterIncidentDevices?.length ||
                  this.filterDevices?.length
                ) {
                  this.totalFiteredCurrentStates =
                    this.sortedCurrentStateList.length;
                }
              }

              this.endingProgress();
            });
        })
    );
    if (
      !this.filterDevices.length &&
      !this.filterIncidentDevices.length &&
      !isPageChanged
    ) {
      this.subscription.add(
        this._deviceContainerStateService
          .getStateColorsIndicator({
            requestId: 0,
            filterContainers: this.filterContainers,
            containerTypes: this.containerTypes,
            filterLastSites: this.filterLastSites,
            filterStatusDays: this.filterStatusDays,
            filterConfidenceRadius: this.filterConfidenceRadius,
            filterDevicesTypes: this.filterDevicesTypes,
            filterContainerCategory: this.filterContainerCategory,
            filterTorn: this.filterTorn,
            filterUnknown: this.filterUnknown
          })
          .subscribe((result) => {
            this.setTotalCurrentStates(result);
          })
      );
    }
  }

  private isAllOrangeActive(): boolean {
    let allActive: boolean = true;
    // No status checked = show everything, so every colour counts as active.
    const anyShow: boolean = this.statusList.some((status) => status.show);

    for (const status of this.statusList) {
      if (anyShow && !status.show && status.marker.includes("orange"))
        allActive = false;
    }

    return allActive;
  }

  private setTotalCurrentStates(colors): void {
    let totalFilteredcurrentState: number = 0;
    const allOrangeActive = this.isAllOrangeActive();
    // No status checked = no filter = count everything (matches the list).
    const anyShow: boolean = this.statusList.some((status) => status.show);

    for (const status of this.statusList) {
      if (status.show || !anyShow) {
        if (status.marker.includes("orange")) {
          switch (status.flag) {
            case "flagStatic":
              totalFilteredcurrentState += !allOrangeActive
                ? colors.orange.static
                : 0;
              break;
          }
        } else {
          totalFilteredcurrentState += colors[status.marker];
        }
      }
    }
    if (allOrangeActive) {
      totalFilteredcurrentState += colors.orange.total;
    }
    this.totalFiteredCurrentStates = totalFilteredcurrentState;
  }

  public refreshList(event, cType: ContainerTypeDTO) {
    this.fetchingContainer = true;
    cType.checked = event.checked;
    this.store.updateType(cType);
  }

  public navChange(event: any): void {
    // Cross-tab jump to the map tracker detail: deep-links to
    // /map/tracker/:tracker_id (= deviceId), which fetches the device by id.
    // Replaces the old in-memory DTO handoff. List and map share
    // MAP_LIST_FILTER_CONFIG, so those params (and only those) come along — any
    // `pos`/`hrs` from an earlier tracker is dropped, as neither is declared there.
    if (event?.currentState?.deviceId) {
      this.filterSync.navigateCarryingFilters(
        this.route,
        ["/map", "tracker", event.currentState.deviceId],
        MAP_LIST_FILTER_CONFIG
      );
    }
  }

  private updateAlertEvaluater(currentState: CurrentStateDTO) {
    this.subscription.add(
      this._incidentHistoricalControllerService
        .updateAlertEvaluater(currentState)
        .subscribe(() => {})
    );
  }

  protected isAdmin(event: string): boolean {
    return this._userRightsService.getUserRightLevel(event);
  }

  public updateAllIncidentDevices(event) {
    this.allChecked = event.checked;
  }

  public showInfoContainer(event: any, currentState: CurrentStateDTO) {
    if (event && event.target && event.target.className === "imageExport") {
      return;
    }
    this.openDeviceDetail(currentState);
  }

  /**
   * Opens the inline history detail for a device and reflects it in the URL
   * (/list/tracker/:deviceId) so it is deep-linkable and back/forward-aware.
   * State is set from the in-hand DTO first, so the paramMap re-emit hits the
   * openDetailFromRoute guard and short-circuits (no re-fetch, instant open).
   */
  private openDeviceDetail(currentState: CurrentStateDTO): void {
    this.infoCurrentState = currentState;
    this.showInfo = true;
    // Nothing to flag while no status is loaded, and pushing an empty list
    // into the store would persist "[]" and wipe the colour filter.
    if (this.statusList?.length) {
      this.store.setStatus(
        this.statusList.map((filter) => ({
          ...filter,
          showDetail: true
        }))
      );
    }
    this.trackerRoute.goTo(currentState.deviceId);
  }

  /**
   * Opens the detail for a deep-linked / jumped device id. Fetches the current
   * state by id (the detail panel needs a full DTO) and reuses the row-click
   * open path. Falls back to /list if the id is unknown or the fetch fails.
   */
  private openDetailFromRoute(deviceId: string): void {
    if (this.infoCurrentState?.deviceId === deviceId && this.showInfo) {
      return; // already showing this device (row-click or back/forward re-emit)
    }
    this.subscription.add(
      this._deviceContainerStateService
        .getClusterCurrentStates([deviceId])
        .subscribe(
          (result: CurrentStateDTO[]) => {
            if (result && result.length) {
              this.openDeviceDetail(result[0]);
            } else {
              // Unknown device id: tell the user the device is gone and fall
              // back to the plain list.
              this.trackerRoute.notFound();
            }
          },
          () => {
            // Fetch failed (network/backend error): fall back to the list.
            this.trackerRoute.toList();
          }
        )
    );
  }

  public hasHeaderCheckbox(): boolean {
    return !!this.sortedCurrentStateList?.find(
      (currentState) =>
        currentState.statusColor === "ORANGE" ||
        (currentState.statusColor === "RED" &&
          currentState.containerType !== "TORN")
    );
  }

  public resetInfiniteScrollData() {
    this.infiniteScroll?.resetScrollbar();
    this.index = 30;
  }
}
